import styled, { css } from 'styled-components';

import { Flag } from '@xing-com/flag';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { InputArea } from '@xing-com/text-field';
import {
  scale020,
  scale080,
  scale110,
  scale120,
  scale130,
  scale140,
  space3XL,
  spaceL,
  spaceXL,
  spaceXXL,
  space4XL,
  xdlColorBorder,
  xdlColorTextSecondary,
  xdlColorTextTertiary,
  xdlColorSkeletonPrimaryAnimation,
  scale150,
  xdlColorBorderStrong,
  scale010,
  spaceS,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

const InnerContainer = styled.div`
  padding: ${spaceL};
`;

const MembershipFlag = styled(Flag)`
  margin-bottom: ${spaceS};
`;

const HeadlineInput = styled(InputArea)`
  margin-bottom: ${spaceXXL};

  & > textarea {
    background-color: none;
    border: 0;
    font-size: ${scale140};
    font-weight: bold;
    outline: none;
    width: 100%;
    word-break: break-word;
    line-height: ${scale150};

    &::placeholder {
      color: ${xdlColorTextSecondary};
      font-size: ${scale140};
    }
  }
  @media ${mediaSmallOrTiny} {
    margin-bottom: ${spaceXL};

    & > textarea {
      font-size: ${scale130};

      &::placeholder {
        font-size: ${scale130};
      }
    }
  }
`;

const SublineInput = styled(InputArea)`
  margin-bottom: ${spaceXXL};

  & > textarea {
    border: 0;
    font-size: ${scale110};
    outline: none;
    width: 100%;
    word-break: break-word;
    line-height: ${scale130};

    &::placeholder {
      color: ${xdlColorTextSecondary};
      font-size: ${scale110};
    }
  }
`;

const EditorStyle = css`
  .is-editor-empty:first-child::before {
    color: ${xdlColorTextTertiary} !important;
  }

  > * + div {
    margin: ${spaceXXL} 0;

    &.node-xingQuote {
      margin: ${space3XL} 0;
    }
  }

  > * + img {
    margin: ${spaceXXL} 0;
  }

  ul,
  ol {
    line-height: ${scale120};
    margin: ${spaceXL} ${spaceXL} 0;
    padding-left: ${spaceL};

    &:first-child {
      margin-top: 0;
    }

    li:not(:first-child) {
      margin-top: ${spaceL};
    }
  }

  h1,
  h2,
  h3,
  h4 {
    line-height: ${scale130};
    margin-top: ${spaceXXL};

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    font-size: ${scale080};
    margin-top: ${spaceXL};
    line-height: ${scale110};

    &:first-child {
      margin-top: 0;
    }
  }

  blockquote {
    border-left: ${scale020} solid ${xdlColorBorderStrong};
    margin: ${spaceXL} ${space4XL};
    padding-left: ${spaceXL};
    &:first-child {
      margin-top: 0;
    }
    p {
      font-size: ${spaceXL};
      font-style: normal;
      font-weight: 700;
      line-height: 1.25;
    }
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
  }

  div[data-youtube-video] {
    iframe {
      width: 100%;
    }
  }

  .is-editor-empty:first-child::before,
  .node-quote.is-empty::before,
  .node-quoteCaption.is-empty::before,
  .node-imageCaption.is-empty::after,
  .node-videoCaption.is-empty::after {
    color: ${xdlColorSkeletonPrimaryAnimation};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  &.ProseMirror-focused {
    outline: none;

    img {
      &.ProseMirror-selectednode {
        outline: ${scale010} solid ${xdlColorBorder};
      }
    }
  }

  .node-xingImage {
    margin: ${spaceXXL} ${spaceL} ${space3XL};

    img {
      display: block;
      margin: 0px auto;
      max-width: 100%;
      padding: 0;
    }

    figcaption.node-imageCaption {
      -webkit-box-pack: center;
      color: ${xdlColorTextTertiary};
      display: flex;
      justify-content: center;
      margin: ${spaceL} auto 0;
      text-align: start;

      @media ${mediaConfined} {
        max-width: 75%;
      }

      &.hide {
        display: none;
      }
    }
  }

  .node-xingVideo {
    margin: 0 ${spaceXL} ${space3XL} ${spaceXL};

    figcaption.node-videoCaption {
      -webkit-box-pack: center;
      color: ${xdlColorTextTertiary};
      display: flex;
      justify-content: center;
      margin: ${spaceL} auto 0;
      text-align: start;

      @media ${mediaConfined} {
        max-width: 75%;
      }

      &.hide {
        display: none;
      }
    }
  }
`;

const EditorContainer = styled.div`
  .tiptap {
    padding-bottom: ${spaceXL};
    ${EditorStyle}
  }
`;

const CharacterCountStyle = styled.div`
  margin-top: ${spaceL};
  color: ${xdlColorTextSecondary};
  text-align: right;
`;

const EditorFooter = styled.div`
  align-items: center;
  border-top: 1px solid ${xdlColorBorder};
  display: flex;
  gap: ${space3XL};
  margin: 0 ${spaceL};
  padding-top: ${spaceL};
  justify-content: flex-end;
`;

const SavingMessage = styled(BodyCopy)`
  margin: 0 auto 0 0;
`;

export const EditorStyled = {
  EditorStyle,
  EditorContainer,
  CharacterCountStyle,
  MembershipFlag,
  HeadlineInput,
  SublineInput,
  InnerContainer,
  EditorFooter,
  SavingMessage,
};
